/**
 * Language selector dropdown — global, lives in the top nav.
 * Shows native names + flags, persists choice via i18next's
 * localStorage detector, and flips RTL for Arabic automatically.
 */
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export default function LanguageSelector({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[var(--t-border)] hover:border-[var(--t-blue)] hover:bg-[rgba(58,160,255,0.06)] transition font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--t-text-dim)] hover:text-[var(--t-cyan)]"
        title={t("lang.selectorTitle")}
        aria-label={t("lang.selectorLabel")}
        aria-expanded={open}
        data-testid="language-selector-toggle"
      >
        <Globe className="w-3.5 h-3.5" strokeWidth={1.7} />
        {compact ? (
          <span className="text-base leading-none">{current.flag}</span>
        ) : (
          <>
            <span className="text-base leading-none">{current.flag}</span>
            <span className="hidden md:inline">{current.code.toUpperCase()}</span>
          </>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 max-h-[70vh] overflow-y-auto bg-[#050912] border border-[var(--t-border-glow)] shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50"
          data-testid="language-selector-menu"
        >
          <div className="px-3 py-2 border-b border-[var(--t-border)] font-mono text-[9px] tracking-[0.28em] uppercase text-[var(--t-blue-glow)]">
            // {t("lang.selectorLabel")}
          </div>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => choose(lang.code)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition border-l-2 ${
                  active
                    ? "border-[var(--t-cyan)] bg-[rgba(58,160,255,0.1)] text-[var(--t-cyan)]"
                    : "border-transparent hover:bg-[rgba(58,160,255,0.05)] text-[var(--t-text-dim)] hover:text-[var(--t-text)]"
                }`}
                data-testid={`language-option-${lang.code}`}
              >
                <span className="flex items-center gap-2.5">
                  <span className="text-lg leading-none">{lang.flag}</span>
                  <span className="flex flex-col leading-tight">
                    <span className={`text-sm ${lang.rtl ? "text-right" : ""}`} dir={lang.rtl ? "rtl" : "ltr"}>
                      {lang.native}
                    </span>
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[var(--t-text-muted)]">
                      {lang.name}
                    </span>
                  </span>
                </span>
                {active && <Check className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
